import React, { useState, memo } from 'react'
import { BaseDrawer } from '@/components/drawer/base-drawer'
import { layerManage, showLayer } from '@/utils/layer'
import { Column, Row, Expaned } from '@/components/layout/layout'

import './purchase-supplier-detail.scss'
import { getBEMCls } from '@/utils/classname'
import { ISupplier } from '@/model/types/supllier'
import $msg from '@/utils/message'
import { $post } from '@/api'
import { IPagination } from '@/model/types/common'
import { useRequest, usePersistFn } from 'ahooks'
import { IPurchaseOrder } from '../../../purchase-order/modules/purchase-table/purchase-table'
import FormTitle from '../../../../components/form/form-title/form-title'
import Table, { ColumnsType } from 'antd/lib/table'
import { WithScrollLoad } from '@/utils/utils-widget'
import PurchaseDetail from '../../../purchase-order/modules/purchase-detail/purchase-detail'
import { Spin } from 'antd'
import dayjs from 'dayjs'
import useCopy from '@/utils/hooks/useCopy'
import Iconfont from '@/components/iconfont/iconfont'
import Button from '@/components/button/button'
import { staffStore } from '@/store/staff'

interface PurchaseSupplierDetailProps {
    supplierId: number
    refresh: () => void
}

const getCls = (e?: string, m?: string) => getBEMCls('purchase-supplier-detail', e, m)

const SupplierInfo = memo(({ supplierInfo, purchaseNum }: { supplierInfo: ISupplier | null; purchaseNum: number }) => {
    const copy = useCopy()

    return supplierInfo ? (
        <>
            <Column data-align-items-center data-padding-top-32 data-padding-bottom-26>
                <div className={getCls('avatar')}>{supplierInfo.name[0]}</div>
                <div className={getCls('name')}>{supplierInfo.name}</div>
                {supplierInfo.phone && (
                    <Row data-align-items-center>
                        <div data-color-999>联系方式：</div>
                        <div data-color-333>{supplierInfo.phone}</div>
                        <div data-cursor-pointer data-margin-left-6 onClick={() => copy(supplierInfo.phone)}>
                            <Iconfont name="icon6fuzhi-20" size={20} />
                        </div>
                    </Row>
                )}
            </Column>
            <FormTitle hasLine={false} title={`采购记录（${purchaseNum}次）`}></FormTitle>
        </>
    ) : (
        <div />
    )
})

const PurchaseSupplierDetail: React.FC<PurchaseSupplierDetailProps> = props => {
    const hasCostPermission = !!staffStore.canIUse(39)
    const [supplierInfo, setSupplierInfo] = useState<ISupplier | null>(null)
    const [purchaseNum, setPurchaseNum] = useState<number>(0)

    const requestHandle = useRequest<
        {
            list: IPurchaseOrder[]
            pagination: IPagination
            purchase_order_count: number
            supplier_info: ISupplier
        },
        {
            supplier_info: ISupplier
            pagination: IPagination
            purchase_order_count: number
            purchase_orders: IPurchaseOrder[]
        }
    >(
        val => {
            return $post({
                url: '/warehouse/getshopsupplierdetail',
                params: {
                    limit: 20,
                    page: val?.pagination?.page ? val.pagination.page + 1 : 1,
                    supplier_id: props.supplierId,
                },
            })
        },
        {
            isNoMore: data => {
                if (data?.pagination) {
                    return data?.pagination.page === data?.pagination.page_count
                } else {
                    return false
                }
            },
            loadMore: true,
            formatResult: data => {
                return {
                    list: data.purchase_orders,
                    supplier_info: data.supplier_info,
                    purchase_order_count: data.purchase_order_count,
                    pagination: data.pagination,
                }
            },
            onSuccess: res => {
                setSupplierInfo(res.supplier_info)
                setPurchaseNum(res.purchase_order_count)
            },
            onError: e => {
                $msg('error', e.message)
            },
        },
    )

    const formatTime = usePersistFn((time: string) => dayjs(time).format('MM-DD'))

    const columns: ColumnsType<IPurchaseOrder> = [
        {
            title: '#',
            key: 'index',
            width: 50,
            render: (value, record, index) => index + 1,
        },
        {
            title: '采购时间',
            key: 'purchase_time',
            dataIndex: 'purchase_time',
            render: val => formatTime(val),
        },
        {
            title: '商品数',
            key: 'order_goods_count',
            dataIndex: 'order_goods_count',
        },
        {
            title: '采购金额',
            key: 'purchase_subtotal',
            dataIndex: 'purchase_subtotal',
            align: 'right',
            render: val => (hasCostPermission ? (val == null ? '-' : `¥${val}`) : '***'),
        },
        {
            title: '到货时间',
            key: 'eta',
            dataIndex: 'eta',
            render: val => formatTime(val),
        },
        {
            title: '状态',
            key: 'stocked_state',
            dataIndex: 'stocked_state',
            width: 74,
            render: (val, record) => {
                if (record.is_draft) {
                    return <span className="danger-color">草稿</span>
                } else {
                    switch (val) {
                        case 0:
                            return <span className="them-color">待入库</span>
                        case 1:
                            return <span className="them-color">部分入库</span>
                        case 2:
                            return <span>已入库</span>
                    }
                }
            },
        },
        {
            title: '操作',
            key: 'is_draft',
            width: 58,
            align: 'center',
            render: (val, record) => (
                <span
                    className="text-btn-blue"
                    onClick={() => {
                        showLayer(<PurchaseDetail orderId={record.id} />)
                    }}
                >
                    详情
                </span>
            ),
        },
    ]

    const handleRomoveSupplier = useRequest(
        () =>
            $post({
                url: '/warehouse/deletesupplier',
                params: {
                    supplier_id: props.supplierId,
                },
            }),
        {
            manual: true,
            onSuccess: () => {
                props.refresh()
                $msg('success', '删除成功')
                layerManage.pop()
            },
            onError: e => $msg('error', e.message),
        },
    )

    return (
        <BaseDrawer
            title="供货商详情"
            isShow
            width={540}
            onClose={() => {
                layerManage.pop()
            }}
        >
            <Column data-height-100p>
                <Expaned data-overflow-auto>
                    <Spin spinning={requestHandle.loading}>
                        <SupplierInfo supplierInfo={supplierInfo} purchaseNum={purchaseNum} />
                        <div className={getCls('table-container')}>
                            <WithScrollLoad data-expaned data-overflow-hidden scrollloadhandle={requestHandle}>
                                <Table
                                    rowKey={record => record.id}
                                    loading={requestHandle.loadingMore || void 0}
                                    bordered
                                    pagination={false}
                                    columns={
                                        hasCostPermission
                                            ? columns
                                            : columns.filter(column => column.key !== 'purchase_subtotal')
                                    }
                                    dataSource={requestHandle.data?.list}
                                    scroll={{ x: 'max-content', y: 306 }}
                                />
                            </WithScrollLoad>
                        </div>
                    </Spin>
                </Expaned>

                <div className={getCls('footer')}>
                    <Button
                        loading={handleRomoveSupplier.loading || void 0}
                        types="error"
                        onClick={handleRomoveSupplier.run}
                    >
                        删除供货商
                    </Button>
                </div>
            </Column>
        </BaseDrawer>
    )
}

export default PurchaseSupplierDetail
